import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Illustration,
  Loader,
  Typography,
} from '@saviynt/design-system';

import {
  getBaseUrlObNextGen,
  getObNextGenApiUrlLists,
} from '../../../constants/obNextGenConstants';
import useLocalization from '../../../hooks/use-localize';
import Logger from '../../../services/loggerService';
import { useStartImportMutation } from '../data-store/startImportSlice';

import './DiscoveryImport.css';

interface DiscoveryImportProps {
  appTypeKey: number;
  iobAppKey: number;
  iconLink: string;
  closeStartImportModal?: () => void;
  onImportStatusChange?: (isRunning: boolean) => void;
}

/**
 * Component that triggers a discovery import on mount.
 */
function DiscoveryImport({
  appTypeKey,
  iobAppKey,
  iconLink,
  closeStartImportModal,
  onImportStatusChange,
}: DiscoveryImportProps): JSX.Element {
  const formatMessage = useLocalization();
  const [startImport, { isError, error }] = useStartImportMutation();
  const [isInitialLoading, setIsInitialLoading] = useState<boolean>(true);

  const { GET_APPLICATION_ICONS } = getObNextGenApiUrlLists();

  const handleStartImport = useCallback(async (): Promise<void> => {
    try {
      setIsInitialLoading(true);
      onImportStatusChange?.(true);
      const result = await startImport({
        appTypeKey,
        iobAppKey, // 54,
      }).unwrap();

      Logger.info(result.message);
    } catch (err) {
      const errStr = err as { message?: string };

      Logger.error(`Failed: ${errStr.message ?? 'Unknown error'}`);
    } finally {
      setIsInitialLoading(false);
      onImportStatusChange?.(false);
    }
  }, [appTypeKey, iobAppKey, startImport, onImportStatusChange]);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    handleStartImport();
  }, [handleStartImport]);

  if (isInitialLoading) {
    return (
      <section className='Discovery-start-import-loader'>
        <Loader color='brand' format='main' kind='loop' />
      </section>
    );
  }

  if (isError) {
    return (
      <section className='Discovery-start-import-error-container'>
        <Box className='Discovery-start-import-error-container-Icon'>
          <Illustration kind='Error' />
        </Box>
        <Box className='Discovery-start-import-error-container-errorText'>
          {error &&
          typeof error === 'object' &&
          'status' in error &&
          error.status === 409 ? (
            <Typography kind='h1'>
              {formatMessage(
                'INTEGRATION.DISCOVERY.SETUP.SERVICENOW.JOBCONFLICT',
                'Another Job In Progress'
              )}
            </Typography>
          ) : (
            <Typography kind='h1'>
              {formatMessage(
                'INTEGRATION.DISCOVERY.SETUP.SERVICENOW.IMPORTFAILED',
                'Importing Failed'
              )}
            </Typography>
          )}
          {error &&
          typeof error === 'object' &&
          'status' in error &&
          error.status === 409 ? (
            <Typography kind='body2' color='--color-foreground-neutral-medium'>
              {formatMessage(
                'INTEGRATION.DISCOVERY.SETUP.SERVICENOW.JOBCONFLICT.SUBHEADING',
                'Another Job In Progress'
              )}
            </Typography>
          ) : (
            <Typography kind='body2' color='--color-foreground-neutral-medium'>
              {formatMessage(
                'INTEGRATIONS.ERROR.CONNECTION',
                'Connection error. Please try again.'
              )}
            </Typography>
          )}
        </Box>
        <Box className='Discovery-start-import-error-container-actions'>
          <Button type='button' kind='filled' onClick={handleStartImport}>
            {formatMessage('INTEGRATIONS.TRY.AGAIN', 'Try Again')}
          </Button>
          <Button type='button' kind='outlined' onClick={closeStartImportModal}>
            {formatMessage('INTEGRATIONS.CLOSE', 'Close')}
          </Button>
        </Box>
      </section>
    );
  }

  return (
    <Box className='Discovery-start-import'>
      <section className='Discovery-start-import-successContainer'>
        <Box className='Discovery-start-import-successContainer-loaderWrapper'>
          <Loader
            color='brand'
            format='inline'
            kind='loop'
            className='Discovery-start-import-successContainer-loaderWrapper-loader'
          />
          <img
            className='Discovery-start-import-successContainer-loaderWrapper-appImage'
            src={`${getBaseUrlObNextGen()}${GET_APPLICATION_ICONS}/${iconLink}`}
            alt='Application icon'
          />
        </Box>
        <Box className='Discovery-start-import-successContainer-content'>
          <Typography kind='h1'>
            {formatMessage(
              'INTEGRATIONS.DISCOVERY.IMPORTINGASSETS',
              'Importing Assets from ServiceNow'
            )}
          </Typography>
          <Typography kind='body2' color='--color-foreground-neutral-medium'>
            {formatMessage(
              'INTEGRATIONS.DISCOVERY.IMPORTINGASSETS.MESSAGE',
              'You can exit the page while waiting. We’ll notify you once the process is complete.'
            )}
          </Typography>
        </Box>
        <Button type='button' kind='filled' onClick={closeStartImportModal}>
          {formatMessage('INTEGRATIONS.CLOSE', 'Close')}
        </Button>
      </section>
    </Box>
  );
}

DiscoveryImport.defaultProps = {
  closeStartImportModal: () => {},
  onImportStatusChange: () => {},
};

export default DiscoveryImport;
